import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatTickerToUppercase } from '@benzinga/utils';

import { deriveQuoteActiveTabFromPathname } from './active-tab';
import type { ActiveTab } from './types';

export const QUOTE_TAB_TO_CHANNEL: Partial<Record<ActiveTab, string>> = {
  news: 'News',
  'analyst-ratings': 'Ratings',
  dividends: 'Dividend',
  earnings: 'Earnings',
  events: 'Events',
  guidance: 'Guidance',
  'insider-trades': 'Insider Trades',
  'short-interest': 'Short Interest',
  'option-chain': 'Options',
  'earnings-forecasts': 'Earnings',
  financials: 'Financials',
  'price-targets': 'Ratings',
  ownership: 'Insider Trades',
  sentiment: 'Sentiment',
  'compare-peers': 'Compare',
};

export const ZACKS_QUOTE_TABS: ReadonlySet<ActiveTab> = new Set<ActiveTab>([
  'earnings',
  'analyst-ratings',
  'price-targets',
]);

export type QuotePageTargeting = {
  BZ_PTYPE: 'ticker';
  BZ_TICKER?: string;
  BZ_CHANNEL?: string;
  md_key?: 'ZKCovered';
};

function normalizeQuoteTickerFromPath(tickerSegment: string): string {
  return formatTickerToUppercase(sanitizeHTML(tickerSegment)) || '';
}

export function buildQuotePageTargeting(
  symbol: string,
  activeTab: ActiveTab,
  options?: { isZacksApplied?: boolean },
): QuotePageTargeting {
  const targeting: QuotePageTargeting = {
    BZ_PTYPE: 'ticker',
    BZ_TICKER: symbol,
  };

  const channel = QUOTE_TAB_TO_CHANNEL[activeTab];
  if (channel) {
    targeting.BZ_CHANNEL = channel;
  }

  if (options?.isZacksApplied && ZACKS_QUOTE_TABS.has(activeTab)) {
    targeting.md_key = 'ZKCovered';
  }

  return targeting;
}

export function getQuotePageTargetingFromPathname(pathname: string): QuotePageTargeting | null {
  if (pathname === '/quote' || pathname === '/quote/') {
    return { BZ_PTYPE: 'ticker' };
  }

  if (!pathname.startsWith('/quote/')) {
    return null;
  }

  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments[0] !== 'quote' || pathSegments.length < 2) {
    return null;
  }

  const symbol = normalizeQuoteTickerFromPath(pathSegments[1]);
  if (!symbol) {
    return { BZ_PTYPE: 'ticker' };
  }

  const activeTab = deriveQuoteActiveTabFromPathname(pathname);
  return buildQuotePageTargeting(symbol, activeTab);
}
